import {
  Component, OnInit, OnDestroy, HostListener
} from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  forkJoin, combineLatest, Observable, Subject
} from 'rxjs';
import {
  filter, map, tap, switchMap, takeUntil
} from 'rxjs/operators';

import { ReferenceDataHttpService, ReferenceDatum, ReferenceDataTypes } from '@sondermind/data-access/reference-data';
import { SondermindApplicationService } from '@sondermind/data-access/sondermind-app-service';

import { IrisButtonTypes } from '@sondermind/iris/button';

import { IAPIFetchResponse, IAPIListResponse } from '@sondermind/utilities/models-http';
import { ProviderUser } from '@sondermind/utilities/models-user-provider';

import {
  MemberAgeService,
  MemberAge,
  ProviderReligionsService,
  IReligionDatum,
  ISexualityDatum,
  ProviderSexualitiesService,
  ProviderLanguagesService,
  LanguageDatum,
  ProviderModalitiesService,
  IModalityDatum
} from '@sondermind/data-access/provider-facing/provider-profile';
import { IComponentCanDeactivate } from '@sondermind/features/provider-facing/provider-profile';

@Component({
  selector: 'member-profile-focus',
  templateUrl: './profile-focus.component.html',
  styleUrls: [
    './profile-focus.component.scss',
    '../../styles/profile-app-shared-styles.scss'
  ],
  standalone: false
})
export class ProfileFocusPage implements OnInit, OnDestroy, IComponentCanDeactivate {
  agesForm: UntypedFormGroup;
  agesFormReset: {};
  ages: ReferenceDatum[];

  user: ProviderUser = null;

  modalitiesForm: UntypedFormGroup = null;
  modalitiesFormReset: any = null;
  sexualitiesForm: UntypedFormGroup = null;
  sexualitiesFormReset: any = null;
  religionsForm: UntypedFormGroup = null;
  religionsFormReset: any = null;

  saving = false;
  loaded = false;
  servicesFormChecked: boolean = false;
  languageFormChecked: boolean = false;

  languageForm: UntypedFormGroup;
  // Assigned type any, because keys are numeric and dynamic for this object
  languageFormReset: any;
  languages: ReferenceDatum[];

  private destroyed$ = new Subject<void>();
  private refLanguages$: Observable<ReferenceDatum[]> = this.refDataService
    .list(
      ReferenceDataTypes.languages,
      {
        filter: [{ key: 'hidden', value: 'false' }],
        sort: [{ key: 'title', asc: true }],
        page: 'disabled'
      }
    )
    .pipe(
      map((languages) => languages.data),
      // eslint-disable-next-line no-return-assign
      tap((languages) => this.languages = languages),
      takeUntil(this.destroyed$)
    );

  modalities$: Observable<ReferenceDatum[]> = this.refDataService
    .list(
      ReferenceDataTypes.modalities,
      {
        filter: [{ key: 'hidden', value: 'false' }],
        sort: [{ key: 'title', asc: true }],
        page: 'disabled'
      }
    ).pipe(
      map((modalities) => modalities.data),
      takeUntil(this.destroyed$)
    );

  sexualities$: Observable<ReferenceDatum[]> = this.refDataService
    .list(
      ReferenceDataTypes.sexualities,
      {
        filter: [{ key: 'hidden', value: 'false' }],
        sort: [{ key: 'title', asc: true }],
        page: 'disabled'
      }
    ).pipe(
      map((sexualities) => sexualities.data),
      takeUntil(this.destroyed$)
    );

  userModalities$ = this.modalitiesService.index({ page: 'disabled' });

  religions$: Observable<ReferenceDatum[]> = this.refDataService
    .list(
      ReferenceDataTypes.religions,
      {
        filter: [{ key: 'hidden', value: 'false' }],
        sort: [{ key: 'title', asc: true }],
        page: 'disabled'
      }
    ).pipe(
      map((religions) => religions.data),
      takeUntil(this.destroyed$)
    );

  userSexualities$ = this.sexualitiesService.index({ page: 'disabled' });

  userReligions$ = this.religionsService.index({ page: 'disabled' });

  userLanguages$ = this.langService.index({ page: 'disabled' });
  IrisButtonTypes = IrisButtonTypes;
  constructor(
    private builder: UntypedFormBuilder,
    private appService: SondermindApplicationService,
    private snackBar: MatSnackBar,
    private memberAgeService: MemberAgeService,
    private religionsService: ProviderReligionsService,
    private sexualitiesService: ProviderSexualitiesService,
    private modalitiesService: ProviderModalitiesService,
    private refDataService: ReferenceDataHttpService,
    private langService: ProviderLanguagesService
  ) {
    this.modalitiesForm = builder.group({});
    this.sexualitiesForm = builder.group({});
    this.agesForm = builder.group({});
    this.religionsForm = builder.group({});
    this.languageForm = this.builder.group({});
  }

  ngOnInit(): void {
    const user$ = this.appService.currentUser$.pipe(filter((u) => !!u));
    // eslint-disable-next-line no-return-assign
    user$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((u) => {
      this.user = u;
    });

    // TODO: Make this more datum type agnostic?
    const ages$ = this.refDataService
      .list(
        ReferenceDataTypes.ages,
        {
          filter: [{ key: 'hidden', value: 'false' }],
          sort: [{ key: 'order_number', asc: true }],
          page: 'disabled'
        }
      ).pipe(
        tap((ages) => {
          this.ages = ages.data;
        })
      );

    const modalitiesUnion$ = combineLatest([this.modalities$, this.userModalities$]);
    const religionsUnion$ = combineLatest([this.religions$, this.userReligions$]);
    const sexualitiesUnion$ = combineLatest([this.sexualities$, this.userSexualities$]);
    const languageUnion$ = combineLatest([this.refLanguages$, this.userLanguages$]);

    const data$ = user$.pipe(
      switchMap((u) => forkJoin([
        this.memberAgeService.index(u.id)
      ]))
    );

    ages$.pipe(
      takeUntil(this.destroyed$),
      switchMap(
        () => combineLatest([data$, modalitiesUnion$, religionsUnion$, sexualitiesUnion$, languageUnion$])
      )
    ).subscribe(
      ([r, m, rel, s, l]) => this.processInitialResponse(r[0], m, rel, s, l)
    );

    this.initializeServicesFormSubscription();
    this.initializeLanguageFormSubscription();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  // @HostListener allows us to also guard against browser refresh, close, etc.
  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.modalitiesForm?.pristine &&
      this.sexualitiesForm?.pristine &&
      this.agesForm?.pristine &&
      this.religionsForm?.pristine &&
      this.languageForm?.pristine;
  }

  initializeServicesFormSubscription(): void {
    this.modalitiesForm.valueChanges.pipe(
      takeUntil(this.destroyed$)
    ).subscribe(() => {
      let checked;
      // eslint-disable-next-line no-restricted-syntax
      for (const ctrl in this.modalitiesForm.controls) {
        if (this.modalitiesForm.controls[ctrl].value === true) checked = true;
      }
      this.servicesFormChecked = checked;
    });
  }

  initializeLanguageFormSubscription(): void {
    this.modalitiesForm.valueChanges.pipe(
      takeUntil(this.destroyed$)
    ).subscribe(() => {
      this.servicesFormChecked = setChecked(this.modalitiesForm);
    });
    this.languageForm.valueChanges.pipe(
      takeUntil(this.destroyed$)
    ).subscribe(() => {
      this.languageFormChecked = setChecked(this.languageForm);
    });
    function setChecked(form: UntypedFormGroup): boolean {
      let checked;
      // eslint-disable-next-line no-restricted-syntax
      for (const ctrl in form.controls) {
        if (form.controls[ctrl].value === true) checked = true;
      }
      return checked;
    }
  }

  initializeRequiredFormSubscriptions(): void {
    this.languageForm.valueChanges.pipe(
      takeUntil(this.destroyed$)
    ).subscribe(() => {
      let checked;
      // eslint-disable-next-line no-restricted-syntax
      for (const ctrl in this.languageForm.controls) {
        if (this.languageForm.controls[ctrl].value === true) checked = true;
      }
      this.languageFormChecked = checked;
    });
  }

  onCancel() {
    this.agesForm.reset(this.agesFormReset);
    this.modalitiesForm.reset(this.modalitiesFormReset);
    this.religionsForm.reset(this.religionsFormReset);
    this.sexualitiesForm.reset(this.sexualitiesFormReset);
    this.languageForm.reset(this.languageFormReset);
  }

  onSave() {
    if (this.saving || !this.servicesFormChecked || !this.languageFormChecked) {
      this.snackBar.open('Error saving. Please check required fields on Client focus.', 'Dismiss', { duration: 30000, panelClass: 'toast-color' });
      return;
    }

    this.saving = true;

    const agesIds = Object.keys(this.agesForm.controls).filter((key) => this.agesForm.controls[key].value);
    const modalitiesIds = Object.keys(this.modalitiesForm.controls).filter((key) => this.modalitiesForm.controls[key].value);
    const religionsIds = Object.keys(this.religionsForm.controls).filter((key) => this.religionsForm.controls[key].value);
    const sexualitiesIds = Object.keys(this.sexualitiesForm.controls).filter((key) => this.sexualitiesForm.controls[key].value);
    const checkedLanguageIds = Object.keys(this.languageForm.controls)
      .reduce<number[]>((acc, currentValue) => {
      if (this.languageForm.controls[currentValue].value) {
        acc.push(+currentValue);
      }
      return acc;
    }, []);

    const update$ = forkJoin([
      this.memberAgeService.update(this.user.id, agesIds.map((x) => +x)),
      this.modalitiesService.update(this.user.id, { ids: modalitiesIds }),
      this.religionsService.update(this.user.id, { ids: religionsIds }),
      this.sexualitiesService.update(this.user.id, sexualitiesIds),
      this.langService.update(this.user.id, checkedLanguageIds)
    ]);

    update$.subscribe({
      next: (r) => {
        this.processUpdateResponse(r[0]);
        this.modalitiesForm.markAsPristine();
        this.sexualitiesForm.markAsPristine();
        this.agesForm.markAsPristine();
        this.religionsForm.markAsPristine();
        this.languageForm.markAsPristine();
      },
      error: (err) => {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    });
  }

  private processInitialResponse(
    agesR: IAPIListResponse<MemberAge>,
    [refModalities, userModalities]: [ReferenceDatum[], IAPIListResponse<IModalityDatum>],
    [refReligions, userReligions]: [ReferenceDatum[], IAPIListResponse<IReligionDatum>],
    [refSexualities, userSexualities]: [ReferenceDatum[], IAPIListResponse<ISexualityDatum>],
    [refLanguages, userLanguages]: [ReferenceDatum[], IAPIListResponse<LanguageDatum>]
  ) {
    refModalities.forEach((modality) => {
      const ctrl = new UntypedFormControl(userModalities.data.some((m) => m.reference_data_id === modality.id));
      this.modalitiesForm.addControl(String(modality.id), ctrl);
    });

    this.modalitiesFormReset = this.modalitiesForm.value;

    this.initializeDatumForm(this.agesForm, this.ages, agesR.data);
    this.agesFormReset = this.agesForm.value;

    refSexualities.forEach((sexuality) => {
      const ctrl = new UntypedFormControl(userSexualities.data.some((m) => m.reference_data_id === sexuality.id));
      this.sexualitiesForm.addControl(String(sexuality.id), ctrl);
    });
    this.sexualitiesFormReset = this.sexualitiesForm.value;

    refReligions.forEach((religion) => {
      const ctrl = new UntypedFormControl(userReligions.data.some((r) => r.reference_data_id === religion.id));
      this.religionsForm.addControl(String(religion.id), ctrl);
    });
    this.religionsFormReset = this.religionsForm.value;

    refLanguages.forEach((lang) => {
      const ctrl = new UntypedFormControl(userLanguages.data.some((m) => m.reference_data_id === lang.id));
      this.languageForm.addControl(String(lang.id), ctrl);
    });
    this.languageFormReset = this.languageForm.value;

    this.loaded = true;
  }

  private processUpdateResponse(
    agesR: IAPIFetchResponse<MemberAge[]>
  ) {
    this.initializeDatumForm(this.agesForm, this.ages, agesR.data);
    this.agesFormReset = this.agesForm.value;

    this.modalitiesFormReset = this.modalitiesForm.value;

    this.religionsFormReset = this.religionsForm.value;

    this.sexualitiesFormReset = this.sexualitiesForm.value;

    this.languageFormReset = this.languageForm.value;

    // kick off reload so we get new profile submission errors
    this.appService.reloadCurrentUser();

    this.snackBar.open('Your client focus has been saved.', 'Dismiss');
    this.saving = false;
  }

  // Used with Reference Datum checklist, the new core services/endpoints -- continue to migrate over to this
  private initializeDatumForm(form: UntypedFormGroup, refData: ReferenceDatum[], memberData: MemberAge[]) {
    refData.forEach((refDatum) => {
      const ctrl = this.builder.control(this.datumIsChecked(memberData, refDatum));
      form.addControl(String(refDatum.id), ctrl);
    });
  }

  private datumIsChecked(userData: MemberAge[], refDatum: ReferenceDatum) {
    return userData.some((userDatum) => userDatum.referenceDataId === refDatum.id);
  }
}
